/* eslint-disable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-empty-function,@typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms'
import { MatSelect } from '@angular/material/select'
import { MatOption } from '@angular/material/core'
import { Subject } from 'rxjs'
import isArray from 'lodash-es/isArray'

@Component({
  selector: 'lin-multi-select-field',
  templateUrl: './multi-select-field.component.html',
  styleUrls: ['./multi-select-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MultiSelectFieldComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('select') select: MatSelect

  control = new FormControl()
  hiddenItems: { [k: number]: boolean } = {}
  selectedItems: { [k: number]: boolean } = {}

  allSelected = false
  searchValue: string
  isNothingFound: boolean

  @Input() items: any[]
  @Input() valueField: string
  @Input() labelField: string
  @Input() stringIds?: boolean
  @Input() skipApiCall: boolean
  @Input() panelClass: string = 'multi-select-panel'
  private readonly onDestroy$ = new Subject<void>()
  private onChange = (value: any) => {}

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this
    }
  }

  ngOnInit() {
    this.ngControl.control?.valueChanges.subscribe((value) => {
      if (value && value?.length && isArray(value)) {
        this.selectedItems = Object.fromEntries(value?.map((el) => [el, true]))
        this.control.setValue(value.map((el) => ((+el).toString() === el && !this.stringIds ? +el : el)))
      } else {
        this.control.setValue('')
        this.selectedItems = {}
        this.allSelected = false
      }
    })
  }

  ngAfterViewInit(): void {
    this.select._handleKeydown = () => {}
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.items?.length && this.labelField) {
      this.items = [
        ...this.items.sort((a, b) =>
          b.alwaysAtTop ? (a.alwaysAtTop ? 0 : Infinity) : a.alwaysAtTop ? -Infinity : a[this.labelField].localeCompare(b[this.labelField]),
        ),
      ]
    }
    this.filterControlValuesByItemsChanges()
  }

  filterControlValuesByItemsChanges() {
    const { control, items, valueField } = this

    const selectedValues: (string | number)[] = control?.value || []

    const itemValue = (item) => item[valueField]
    const itemValues = items?.map(itemValue) || []
    const isValueStillExists = (value: string | number) => itemValues.includes(value)
    const filteredValues = selectedValues.filter(isValueStillExists)

    control.setValue(filteredValues)
  }

  onOptionClick(value: number) {
    this.selectedItems[value] ? delete this.selectedItems[value] : (this.selectedItems[value] = true)

    this.allSelected = Object.keys(this.selectedItems).length === this.items.length
  }

  onSearchKeyUp() {
    const { searchValue } = this

    this.hiddenItems = {}

    const isHiddenValue = (item) => searchValue && !item[this.labelField].toLowerCase().includes(searchValue.toLowerCase())
    const setHidden = (item) => (this.hiddenItems[item.id] = true)

    this.items.filter(isHiddenValue).forEach(setHidden)

    this.isNothingFound = this.items.length === Object.keys(this.hiddenItems).length
  }

  onSelectOpenedChange(): void {
    if (!this.select.panelOpen && Object.keys(this.selectedItems)) {
      sessionStorage.setItem('skipApiCall', JSON.stringify(this.skipApiCall))
    }

    !this.select.panelOpen && this.onChange(Object.keys(this.selectedItems))

    if (!this.searchValue) return

    this.searchValue = ''
    this.onSearchKeyUp()
  }

  toggleAllSelection(): void {
    const optionsSelection = (item: MatOption) => (this.allSelected ? item.select() : item.deselect())

    if (this.allSelected) {
      const itemToSelected = (acc, item) => {
        acc[item[this.valueField]] = true
        return acc
      }

      this.selectedItems = this.items.reduce(itemToSelected, {})
    } else {
      this.selectedItems = {}
    }

    this.select.options.forEach(optionsSelection)
  }

  writeValue(obj: any): void {
    this.control.setValue(obj)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable()
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
